// V2 /app sign-in state (BRIEFS C2.4, SPEC §5 "Login").
//
// Keeps {user, accessToken} in memory; the device token stays in
// localStorage["v2_device_token"] (see api/v2.js). On load, if a device token exists the
// app refreshes once and is signed in — Harsh logs in only once per laptop.
// status: 'loading' | 'signedIn' | 'signedOut' | 'offline' (server not reachable; retried).
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  deviceLogin, deviceName, onAuthEvent, refreshAccess, tokens,
} from '../api/v2';

const AppAuthContext = createContext(null);
const OFFLINE_RETRY_MS = 10000;

export function AppAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(() => (tokens.device ? 'loading' : 'signedOut'));
  const retryTimer = useRef(null);

  const tryRefresh = useCallback(() => {
    clearTimeout(retryTimer.current);
    refreshAccess()
      .then((data) => { setUser(data.user); setStatus('signedIn'); })
      .catch((err) => {
        if (err && err.signedOut) { setUser(null); setStatus('signedOut'); return; }
        setStatus('offline');
        retryTimer.current = setTimeout(tryRefresh, OFFLINE_RETRY_MS);
      });
  }, []);

  useEffect(() => {
    if (tokens.device) tryRefresh();
    const off = onAuthEvent((ev) => {
      if (ev.type === 'signedOut') { setUser(null); setStatus('signedOut'); }
      if (ev.type === 'user' && ev.user) { setUser(ev.user); setStatus('signedIn'); }
    });
    return () => { off(); clearTimeout(retryTimer.current); };
  }, [tryRefresh]);

  const login = useCallback(async (username, password) => {
    const data = await deviceLogin(username.trim(), password, deviceName());
    setUser(data.user);
    setStatus('signedIn');
    return data.user;
  }, []);

  const value = {
    user,
    status,
    accessToken: tokens.access,
    login,
    retry: tryRefresh,
  };
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export const useAppAuth = () => useContext(AppAuthContext);
